import React from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';

import { Static } from './Static';

const Meta = ({
  title,
  description,
  pages,
  indexable,
  crawlable,
  canonicalUrl,
  images,
  showFollow,
}) => {
  const { query = {}, asPath = '' } = useRouter();
  const { type } = query;

  const isSearchQuery = type === 'search'; // Don't include /s queries in this

  const page = parseInt(query.page || 1, 10);
  const currentPageText = `?page=${page}`;
  const prevPageText = `?page=${page - 1}`;
  const nextPageText = `?page=${page + 1}`;

  const queryPos = asPath.indexOf('?');
  const asPathWithNoQueryParms =
    queryPos > -1 ? asPath.slice(0, queryPos) : asPath;

  const canonical =
    type === 's'
      ? `https://www.johnlewis.com${asPathWithNoQueryParms}`
      : `https://www.johnlewis.com/${canonicalUrl.replace(/^\//, '')}`;

  const currentPageCanonical = `${canonical}${page > 1 ? currentPageText : ''}`;

  const robots = [];
  if (!indexable) {
    robots.push('noindex');
  }
  if (!crawlable) {
    robots.push('nofollow');
  } else if (crawlable && showFollow) {
    robots.push('follow');
  }
  const robotsMetaContent = robots.length > 0 && robots.join(', ');

  return (
    <Head>
      <title>{title}</title>
      {robotsMetaContent && <meta name="robots" content={robotsMetaContent} />}
      <meta name="description" content={description} />
      <meta property="og:site_name" content="John Lewis & Partners" />
      <meta property="og:url" content={currentPageCanonical} />
      {images.length > 0 ? (
        <>
          {images.map((image) => (
            <meta key={image} property="og:image" content={image} />
          ))}
          <meta property="og:image:type" content="image/jpeg" />
        </>
      ) : (
        <>
          <meta
            property="og:image"
            content="https://johnlewis.scene7.com/is/image/JohnLewis/ogFeatureImage-johnLewisPartners-040918"
          />
          <meta property="og:image:type" content="image/png" />
        </>
      )}
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />

      {!isSearchQuery && (
        <>
          <link rel="canonical" href={currentPageCanonical} />
          {page < pages && (
            <link rel="next" href={`${canonical}${nextPageText}`} />
          )}
          {page > 1 && (
            <link
              rel="prev"
              href={`${canonical}${page > 2 ? `${prevPageText}` : ''}`}
            />
          )}
        </>
      )}
      <Static />
    </Head>
  );
};

Meta.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  indexable: PropTypes.bool,
  crawlable: PropTypes.bool,
  canonicalUrl: PropTypes.string,
  images: PropTypes.arrayOf(PropTypes.string),
  showFollow: PropTypes.bool,
};

Meta.defaultProps = {
  indexable: false,
  crawlable: false,
  canonicalUrl: '/',
  images: [],
  showFollow: false,
};

export default Meta;
